import { SignedImg } from "@/components/shared/SignedImg";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, X, Eye, EyeOff, Send, Maximize2 } from "lucide-react";
import { formatDate } from "@/lib/format";

// Prohlížeč: velká fotka v šedém rámu, šipky, pásek náhledů. Pod hlavičkou
// vždy detaily fotky (zakázka · datum · autor · zdroj · popisek) a akce —
// oko do deníku, Poslat, Zvětšit. Klik do fotky otevírá lightbox se zoomem.
// 216: pásek náhledů kreslí jen okno ±20 kolem aktuální fotky (malé náhledy,
// ne originály); `total` = počet fotek ve výběru, i když ještě nejsou načtené.
const STRIP = 20;

export function GalleryViewer({ photos, total, index, onIndex, onClose, canEye, onEye, onSend, onZoom }) {
  const p = photos[index];
  if (!p) return null;
  const lo = Math.max(0, index - STRIP);
  const strip = photos.slice(lo, index + STRIP + 1);
  const go = (d) => onIndex((index + d + photos.length) % photos.length);
  const inDiary = p.status === "approved";
  return (
    <div className="space-y-3" data-testid="gallery-viewer">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm text-muted-foreground shrink-0 tabular-nums" data-testid="viewer-counter">{index + 1} / {Math.max(total || 0, photos.length)}</span>
        <Button variant="ghost" size="sm" onClick={onClose} data-testid="viewer-close"><X size={16} className="mr-1" /> Zpět na mřížku</Button>
      </div>

      {/* Detaily fotky — vždy viditelné, žádné skryté informace. */}
      <div className="flex items-center justify-between gap-3 flex-wrap rounded-[14px] bg-card border border-border px-3 py-2" data-testid="viewer-meta">
        <div className="min-w-0 text-sm">
          <span className="font-bold">{p.project_name || "—"}</span>
          <span className="text-muted-foreground"> · {formatDate(p.date)} · {p.author_name || "—"}</span>
          <span className="ml-2 text-[11px] font-bold px-2 py-0.5 rounded-full bg-secondary text-muted-foreground align-middle">{p.source_label}</span>
          {p.caption && <div className="text-xs text-muted-foreground truncate">{p.caption}</div>}
        </div>
        <div className="flex items-center gap-1 shrink-0 flex-wrap">
          {canEye && (
            inDiary ? (
              <>
                <span className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-brand text-white text-[13px] font-bold" data-testid="viewer-eye-state"
                  title={p.approved_at ? `V deníku od ${formatDate(p.approved_at)}${p.approved_by_name ? ` · ${p.approved_by_name}` : ""}` : "V deníku"}>
                  <Eye size={14} /> V deníku
                </span>
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => onEye(p)} data-testid="viewer-eye">
                  <EyeOff size={15} className="mr-1" /> Skrýt z deníku
                </Button>
              </>
            ) : (
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => onEye(p)} data-testid="viewer-eye">
                <Eye size={15} className="mr-1" /> Zobrazit v deníku
              </Button>
            )
          )}
          {canEye && (
            <Button size="sm" variant="ghost" onClick={() => onSend(p)} data-testid="viewer-send" title="Poslat odkazem"><Send size={15} /></Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => onZoom(p)} data-testid="viewer-zoom" title="Zvětšit na celou obrazovku"><Maximize2 size={15} /></Button>
        </div>
      </div>

      <div className="relative bg-secondary rounded-[16px] flex items-center justify-center" style={{ minHeight: "50vh" }}>
        <button type="button" onClick={() => go(-1)} data-testid="viewer-prev"
          className="absolute left-2 z-10 w-11 h-11 rounded-full bg-black/40 text-white grid place-items-center"><ChevronLeft size={24} /></button>
        <button type="button" onClick={() => onZoom(p)} className="cursor-zoom-in" data-testid="viewer-photo">
          <SignedImg url={p.url} alt={p.caption} className="max-h-[62vh] max-w-full object-contain" />
        </button>
        <button type="button" onClick={() => go(1)} data-testid="viewer-next"
          className="absolute right-2 z-10 w-11 h-11 rounded-full bg-black/40 text-white grid place-items-center"><ChevronRight size={24} /></button>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {strip.map((ph, k) => {
          const i = lo + k;
          return (
            <button key={ph.id} onClick={() => onIndex(i)} data-testid={`viewer-thumb-${ph.id}`}
              className={`shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${i === index ? "border-brand" : "border-transparent"}`}>
              <SignedImg url={ph.url} alt="" thumb={64} className="w-full h-full object-cover" />
            </button>
          );
        })}
      </div>
    </div>
  );
}
